import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Minus, FloppyDisk, CheckCircle } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATS = [
  { id: 'feminino', label: 'Feminino' },
  { id: 'open', label: 'Open' },
  { id: 'misto', label: 'Misto' },
  { id: 'rei_rainha', label: 'Rei e Rainha' },
];

function collectBracketGames(obj, out) {
  if (!obj || typeof obj !== 'object') return;
  if (Array.isArray(obj)) { obj.forEach(x => collectBracketGames(x, out)); return; }
  if (obj.slot_id && (obj.dupla1 !== undefined || obj.dupla2 !== undefined)) {
    out.push({ group: 'Chaveamento', slot_id: obj.slot_id, d1: obj.dupla1, d2: obj.dupla2, score1: obj.score1, score2: obj.score2, phase: 'bracket' });
    return;
  }
  Object.values(obj).forEach(v => collectBracketGames(v, out));
}

function flattenGames(data) {
  if (!data) return [];
  const out = [];
  const groups = data.groups || {};
  const type = data.config?.type;
  if (type && type.startsWith('rei_rainha')) {
    Object.entries(groups).forEach(([gid, gd]) => {
      (gd.rounds || []).forEach(rd => rd.games.forEach(gm => out.push({
        group: `Grupo ${gid} · Rodada ${rd.round}`, slot_id: gm.slot_id, d1: gm.team1, d2: gm.team2, score1: gm.score1, score2: gm.score2, phase: 'rr',
      })));
    });
  } else {
    Object.entries(groups).forEach(([gid, gd]) => {
      const duplas = gd.duplas || [];
      const matches = gd.matches || [];
      for (let i = 0; i < duplas.length; i++) {
        for (let j = i + 1; j < duplas.length; j++) {
          const slot = `${gid}:${i}-${j}`;
          const m = matches.find(x => x.slot_id === slot);
          out.push({ group: `Grupo ${gid}`, slot_id: slot, d1: duplas[i], d2: duplas[j], score1: m?.score1 ?? null, score2: m?.score2 ?? null, phase: 'group' });
        }
      }
    });
  }
  collectBracketGames(data.bracket, out);
  return out;
}

const isPlayed = (g) => g.score1 !== null && g.score1 !== undefined && g.score2 !== null && g.score2 !== undefined;
const isReady = (g) => g.d1 && g.d1 !== '-' && g.d2 && g.d2 !== '-';

function Scorer({ cat, game, onClose, onSaved }) {
  const [s1, setS1] = useState(game.score1 ?? 0);
  const [s2, setS2] = useState(game.score2 ?? 0);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/tk-league/tournament/match`, {
        category: cat, phase: game.phase, slot_id: game.slot_id, dupla1: game.d1, dupla2: game.d2, score1: parseInt(s1), score2: parseInt(s2),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Placar salvo');
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); } finally { setSaving(false); }
  };
  const Side = ({ name, val, setVal, testid }) => (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 py-6">
      <p className="text-lg sm:text-xl font-bold text-white text-center px-2 leading-tight min-h-[3.5rem] flex items-center" data-testid={`${testid}-name`}>{name}</p>
      <div className="text-7xl sm:text-8xl font-black text-[#FFB703] tabular-nums" data-testid={`${testid}-value`}>{val}</div>
      <div className="flex items-center gap-4">
        <button onClick={() => setVal(v => Math.max(0, v - 1))} className="w-16 h-16 rounded-full bg-white/10 text-white flex items-center justify-center active:scale-95 transition-transform" data-testid={`${testid}-minus`}>
          <Minus size={32} weight="bold" />
        </button>
        <button onClick={() => setVal(v => v + 1)} className="w-20 h-20 rounded-full bg-[#E85D04] text-white flex items-center justify-center active:scale-95 transition-transform shadow-lg" data-testid={`${testid}-plus`}>
          <Plus size={40} weight="bold" />
        </button>
      </div>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 bg-[#111] flex flex-col" data-testid="live-scorer">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <button onClick={onClose} className="text-white/70 flex items-center gap-1 text-sm" data-testid="scorer-back"><ArrowLeft size={18} /> Voltar</button>
        <span className="text-xs uppercase tracking-wider text-white/50">{game.group}</span>
        <span className="w-14" />
      </div>
      <div className="flex-1 flex flex-col sm:flex-row items-stretch">
        <Side name={game.d1} val={s1} setVal={setS1} testid="team1" />
        <div className="flex items-center justify-center text-white/30 text-2xl font-bold py-2">×</div>
        <Side name={game.d2} val={s2} setVal={setS2} testid="team2" />
      </div>
      <div className="p-4">
        <button onClick={save} disabled={saving} className="w-full h-16 rounded-2xl bg-[#2D6A4F] text-white text-xl font-bold flex items-center justify-center gap-2 active:scale-[0.99] transition-transform disabled:opacity-60" data-testid="scorer-save">
          <FloppyDisk size={26} weight="fill" /> {saving ? 'Salvando...' : 'Salvar placar'}
        </button>
      </div>
    </div>
  );
}

export default function LiveScore() {
  const [state, setState] = useState(null);
  const [cat, setCat] = useState('feminino');
  const [active, setActive] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tk-league/tournament`);
      setState(data);
    } catch { toast.error('Erro ao carregar'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const games = state ? flattenGames(state[cat]) : [];
  const grouped = games.reduce((acc, g) => { (acc[g.group] = acc[g.group] || []).push(g); return acc; }, {});

  return (
    <div className="min-h-screen bg-[#1A1A1A] text-white" data-testid="live-score-page">
      <header className="sticky top-0 z-40 bg-[#111] border-b border-white/10 px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => navigate('/tk-league/torneio')} className="text-white/70 flex items-center gap-1 text-sm" data-testid="live-exit">
            <ArrowLeft size={18} /> Sair
          </button>
          <h1 className="font-['Outfit'] text-lg font-bold">Placar ao Vivo</h1>
          <span className="w-12" />
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="live-cats">
          {CATS.map(c => (
            <button key={c.id} onClick={() => setCat(c.id)} className={`px-4 py-2 rounded-full font-semibold text-sm whitespace-nowrap ${cat === c.id ? 'bg-[#E85D04] text-white' : 'bg-white/10 text-white/70'}`} data-testid={`live-cat-${c.id}`}>{c.label}</button>
          ))}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-4 space-y-5">
        {!state && <p className="text-center text-white/50 py-10">Carregando...</p>}
        {state && Object.entries(grouped).map(([grp, list]) => (
          <section key={grp}>
            <p className="text-xs uppercase tracking-wider text-white/40 font-semibold mb-2">{grp}</p>
            <div className="space-y-2">
              {list.map(g => {
                const ready = isReady(g);
                const played = isPlayed(g);
                return (
                  <button
                    key={g.slot_id}
                    disabled={!ready}
                    onClick={() => setActive(g)}
                    className={`w-full flex items-center gap-2 rounded-xl px-4 py-3 text-left transition-colors ${ready ? 'bg-white/5 hover:bg-white/10 active:bg-white/15' : 'bg-white/[0.02] opacity-40'}`}
                    data-testid={`live-game-${g.slot_id}`}
                  >
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold break-words leading-tight">{g.d1 || '-'}</span>
                      <span className="block text-[11px] text-white/40">×</span>
                      <span className="block text-sm font-semibold break-words leading-tight">{g.d2 || '-'}</span>
                    </span>
                    <span className="flex-shrink-0 flex items-center gap-2">
                      {played ? (
                        <span className="text-2xl font-black text-[#FFB703] tabular-nums">{g.score1} <span className="text-white/30 text-lg">×</span> {g.score2}</span>
                      ) : (
                        <span className="text-xs text-white/40 border border-white/15 rounded-full px-3 py-1">Lançar</span>
                      )}
                      {played && <CheckCircle size={20} weight="fill" className="text-[#2D6A4F]" />}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </main>

      {active && <Scorer cat={cat} game={active} onClose={() => setActive(null)} onSaved={load} />}
    </div>
  );
}
